import React, { useMemo, useState } from "react";
import copy from "copy-to-clipboard";
import { Copy as CopyIcon, Check, FileText } from "lucide-react";
import { sortBy, uniqBy } from "lodash";

import { Media } from "@/services/bedrock";
import { Dialog, Button } from "@/components/ui";

interface Props {
  open: boolean;
  videos: Media[];
  onClose?: () => void;
}

export const VideoTranscriptsModal: React.FC<Props> = ({ open, videos, onClose }) => {
  const [copiedTranscript, setCopiedTranscript] = useState<boolean>(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number>(0);
  const [selectedTranscriptIndex, setSelectedTranscriptIndex] = useState<number>(0);

  const selectedMedia = useMemo(() => videos[selectedMediaIndex], [videos, selectedMediaIndex]);

  const displayedTranscripts = useMemo(() => {
    if (!selectedMedia?.transcripts) return [];
    const sortedTranscripts = sortBy(selectedMedia.transcripts, t => (t.id ? -t.id : 0));
    const latestByLanguage = uniqBy(sortedTranscripts, "languageCode");
    return sortBy(latestByLanguage, t => (t.isOriginal ? 0 : 1));
  }, [selectedMedia?.transcripts]);

  const selectedTranscript = useMemo(
    () => displayedTranscripts[selectedTranscriptIndex],
    [displayedTranscripts, selectedTranscriptIndex]
  );

  const mediaOptions = useMemo(
    () => videos.map((_, index) => ({ key: index, text: `File ${index + 1}`, value: index })),
    [videos]
  );

  const transcriptOptions = useMemo(() => {
    return displayedTranscripts.map((t, i) => {
      let label = t.languageCode === "en-US" ? "English" : t.languageCode === "es-US" ? "Spanish" : t.languageCode;
      label += t.isOriginal ? " (Original)" : " (Translation)";
      return {
        key: i,
        text: label,
        value: i,
      };
    });
  }, [displayedTranscripts]);

  const copyTranscript = () => {
    const transcript = selectedTranscript?.content;
    if (!transcript) return;

    copy(transcript);
    if (copiedTranscript) return;

    setCopiedTranscript(true);
    setTimeout(() => {
      setCopiedTranscript(false);
    }, 2000);
  };

  const selectedMediaContent = () => {
    const transcript = selectedTranscript?.content;
    if (!transcript) {
      return (
        <div className="text-center italic text-slate-400 py-6 text-xs font-medium">
          No transcript available for this media.
        </div>
      );
    }

    return transcript.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  const mediaDropdown = () => {
    return (
      mediaOptions.length > 1 && (
        <select
          value={selectedMediaIndex}
          onChange={e => {
            setSelectedMediaIndex(Number(e.target.value));
            setSelectedTranscriptIndex(0);
          }}
          className="bg-white border border-slate-200 text-slate-700 rounded-lg px-2.5 py-1.5 text-xs font-semibold outline-none cursor-pointer hover:border-slate-300 transition-colors"
        >
          {mediaOptions.map(opt => (
            <option key={opt.key} value={opt.value}>
              {opt.text}
            </option>
          ))}
        </select>
      )
    );
  };

  const transcriptDropdown = () => {
    return (
      transcriptOptions.length > 1 && (
        <select
          value={selectedTranscriptIndex}
          onChange={e => setSelectedTranscriptIndex(Number(e.target.value))}
          className="bg-white border border-slate-200 text-slate-700 rounded-lg px-2.5 py-1.5 text-xs font-semibold outline-none cursor-pointer hover:border-slate-300 transition-colors"
        >
          {transcriptOptions.map(opt => (
            <option key={opt.key} value={opt.value}>
              {opt.text}
            </option>
          ))}
        </select>
      )
    );
  };

  const copyButton = () => {
    const transcript = selectedTranscript?.content;
    if (!transcript) return null;

    return (
      <Button
        data-elm-id="videoTranscriptsModalCopyBtn"
        variant="outline"
        size="sm"
        className="flex items-center gap-1.5 font-semibold text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50/50 hover:text-indigo-700"
        onClick={copyTranscript}
      >
        {copiedTranscript ? (
          <>
            <Check className="size-3.5" />
            Copied!
          </>
        ) : (
          <>
            <CopyIcon className="size-3.5" />
            Copy Transcript
          </>
        )}
      </Button>
    );
  };

  const closeButton = () => {
    return (
      <Button
        data-elm-id="videoTranscriptsModalCloseBtn"
        variant="default"
        size="sm"
        className="font-semibold text-xs px-4"
        onClick={onClose}
      >
        Close
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose?.();
      }}
      size="xl"
    >
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold">
          <FileText className="size-5 text-indigo-600" />
          <span>Transcript</span>
        </Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="p-5 max-h-[60vh] overflow-y-auto flex flex-col gap-4">
        {(mediaOptions.length > 1 || transcriptOptions.length > 1) && (
          <div className="flex flex-row items-center gap-2.5 select-none border-b border-slate-100 pb-3">
            {mediaDropdown()}
            {transcriptDropdown()}
          </div>
        )}
        <div className="text-slate-600 text-sm whitespace-pre-wrap leading-relaxed select-text font-normal">
          {selectedMediaContent()}
        </div>
      </Dialog.Content>

      <Dialog.Footer>
        {copyButton()}
        {closeButton()}
      </Dialog.Footer>
    </Dialog>
  );
};
